










































import _ from "lodash";
import moment from "moment-timezone";
import sanitize from "sanitize-filename";
import slugify from "slugify";
import * as AmplifyModules from "aws-amplify";

import { Component, Prop, Vue } from "vue-property-decorator";
import { Getter, Action } from "vuex-class";

import { Photo, Maintenancejob } from "@/models";
import PhotoComponent from "@/components/photo/Photo.vue";

const jo = require("jpeg-autorotate");
const piexif = require("piexifjs");

@Component({
  components: {
    PhotoComponent,
  },
})
export default class Photos extends Vue {
  @Getter("maintenancejobs/current") public job: Maintenancejob;
  @Getter("auth/deviceKey") public deviceKey: string;

  @Action("maintenancejobs/setMaintenancejobDeep")
  public setMaintenancejobDeep: (payload: { path: string; data: any }) => Promise<any>;

  @Prop()
  public path: string;

  @Prop()
  public photos: Photo[];
  get _photos(): Photo[] {
    return this.photos;
  }

  @Prop()
  public buttontext: string;

	@Prop({default: true}) public showUploadAction: boolean;
	@Prop({default: true}) public showPhotoActions: boolean;

  get galleryType(): string {
    if (this.path.endsWith("photos.in")) {
      return this.path.endsWith("photos.in") ? "in" : "out";
    }
    return this.path.endsWith("condition.in.photos") ? "in" : "out";
  }

  // Prevent TypeScript from complaining!
  public $Amplify: any;

  public file: FileList | File | null = null;

  public isDragging: boolean = false;

  public uploadingCount: number = 0;

  /**
   * When Component is first mounted.
   */
  public mounted() {
    this.uploadingCount = 0;

    if (!this.deviceKey) {
      //throw Error('Unable to get Device key');
    }
  }

  /**
   * When dragging enters the drop area.
   */
  public onDragOver(event: DragEvent) {
    this.isDragging = true;
  }

  /**
   * When dragging leaves the drop area.
   */
  public onDragLeave(event: MouseEvent) {
    if (event.currentTarget === event.target) {
      this.isDragging = false;
    }
  }

  /**
   * When drop occurs on the drop area.
   */
  public onDrop(event: DragEvent) {
    this.isDragging = false;

    const files: FileList | null = _.get(event, "dataTransfer.files", null);

    this.uploadFiles(files);
  }

  /**
   * When file is uploaded using "+ Photo" button.
   */
  public onFileInputChange({ target }: { target: HTMLInputElement }) {
    const files = _.get(this.$refs.file, "files", null);

    this.uploadFiles(files);
  }

  /**
   * Upload 1 or more files to S3.
   */
  public async uploadFiles(files: FileList | null) {
    try {
      if (null === files || 0 === files.length) {
        return;
      }

      this.uploadingCount = 0;

      // S3 options
      const options = {
        contentType: "image/jpeg",
        customPrefix: {
          public: "",
          protected: "",
          private: "",
        },
        level: "public",
      };

      for (let i = 0; i < files.length; i++) {
        let file: File | null = files.item(i);

        if (file === null) {
          continue;
        }

        const { name, type, lastModified } = file;

        if (type !== "image/jpeg") {
          this.$onecentral.displayError(`${sanitize(name)} is not a JPEG.`);
          continue;
        }

        // Increase uploadingCount (displays loading spinner)
        ++this.uploadingCount;

        // Use ArrayBuffer for jpeg-autorotate
        const arrayBuffer = await file.arrayBuffer();

        let imageBuffer = new Buffer(arrayBuffer);

        // Prevent "Given thumbnail is too large. max 64kB" error
        imageBuffer = this.deleteThumbnailFromExif(imageBuffer);

        // Correct the orientation using jpeg-autorotate
        await jo
          .rotate(imageBuffer, {})
          .then(
            (result: {
              buffer: any;
              orientation: number;
              dimensions: {
                width: number;
                height: number;
              };
              quality: number;
            }) => {
              // console.log(`Orientation was ${result.orientation}`)
              // console.log(`Dimensions after rotation: ${result.dimensions.width}x${result.dimensions.height}`);
              // console.log(`Quality: ${result.quality}`);

              /**
               * The 8 EXIF orientation values are numbered 1 to 8:
               *
               * 1 = 0 degrees: the correct orientation, no adjustment is required.
               * 2 = 0 degrees, mirrored: image has been flipped back-to-front.
               * 3 = 180 degrees: image is upside down.
               * 4 = 180 degrees, mirrored: image has been flipped back-to-front and is upside down.
               * 5 = 90 degrees: image has been flipped back-to-front and is on its side.
               * 6 = 90 degrees, mirrored: image is on its side.
               * 7 = 270 degrees: image has been flipped back-to-front and is on its far side.
               * 8 = 270 degrees, mirrored: image is on its far side.
               */
              if (result.orientation > 1) {
                // Recreate File from jpeg-autorotate Buffer data
                const newFileBuffer = Buffer.from(result.buffer);

                // Update the File we will send to S3
                file = new File([newFileBuffer], name, {
                  type: type,
                  lastModified: lastModified,
                });
              }
            }
          )
          .catch((error: any) => {
            switch (error.code) {
              // Some errors should not halt processing
              case jo.errors.read_exif: // EXIF data could not be read
              case jo.errors.no_orientation: // No orientation tag was found
              case jo.errors.correct_orientation: // The image orientation is already correct
                console.info(name, error.message, error);
                break;
              // Rethrow all other errors
              case jo.errors.read_file: // File could not be opened
              case jo.errors.unknown_orientation: // The orientation tag is unknown
              case jo.errors.rotate_file: // An error occurred when rotating the image
              default:
                throw error;
            }
          });

        // Add timestamp to make each device upload unique, and sanitise input filename
        let deviceKeyPrefix = `${this.deviceKey}`;
        if (!this.deviceKey) deviceKeyPrefix = `jobs`;
        else deviceKeyPrefix = `${this.deviceKey}`;
        const s3Filepath = `${deviceKeyPrefix}/${moment().format(
          "x"
        )}/${slugify(sanitize(name))}`;
        // Save the File to S3
        await (this.$Amplify.Storage as AmplifyModules.StorageClass)
          .put(s3Filepath, file, options)
          .then(({ key }: any) => {
            if (!key || !key.length) {
              --this.uploadingCount;
              throw Error("Could not determine uploaded URL");
            }

            // Create the Photo from the S3 response
            const photo = new Photo({
              src: this.$onecentral.s3Origin(key),
              createdAt: this.$onecentral.datetimeToUTC(lastModified),
              // We only display the link to the Gallery when the parent Maintenancejob has been saved
              hasGalleryLink: false,
            });

            // Add Photo to photos array         
            const photos = _.get(this.job, this.path).concat([photo]);
            

            // Update Vuex
            this.setMaintenancejobDeep({ path: this.path, data: photos });

            --this.uploadingCount;
          })
          .catch((error: Error) => {
            // Adjust uploadingCount and rethrow error
            --this.uploadingCount;
            throw error;
          });
      }
    } catch (error) {
      this.$onecentral.displayError(error);
    }
  }

  /**
   * Remove the thumbnail from an image.
   *
   * This prevents the "Given thumbnail is too large. max 64kB" error.
   *
   * @link https://github.com/johansatge/jpeg-autorotate#thumbnail-too-large
   *
   * @param {Buffer} imageBuffer Image Buffer
   *
   * @returns {Buffer}
   */
  deleteThumbnailFromExif(imageBuffer: Buffer) {
    const imageString = imageBuffer.toString("binary");
    const exifObj = piexif.load(imageString);
    delete exifObj.thumbnail;
    delete exifObj["1st"];
    const exifBytes = piexif.dump(exifObj);
    const newImageString = piexif.insert(exifBytes, imageString);
    return Buffer.from(newImageString, "binary");
  }

  /**
   * Rotate a Photo.
   *
   * A single rotation is always 90° clockwise.
   *
   * @param {number} index index of Photo to rotate
   */
  rotatePhoto(index: number) {
    let photo = this._photos[index];
    let { rotate } = photo;

    // init rotate property if it doesn't exist
    if (rotate === undefined) {
      rotate = 0;
    }

    // Rotate by 90°, convert 360° to 0°
    rotate = (rotate + 90) % 360;

    // remove rotate property if we are back to 0°
    if (rotate) {
      photo.rotate = rotate;
    } else {
      delete photo.rotate;
    }

    // Update photos array
    this._photos.splice(index, 1, photo);

    // Update vuex
    this.setMaintenancejobDeep({ path: this.path, data: this._photos });
  }

  /**
   * Delete a Photo.
   *
   * Note this only removes the Photo reference from the data in Vuex.
   * It DOES NOT delete the actual file from file storage (e.g. from FTP/S3).
   *
   * @param {number} index Index of Photo to delete
   */
  deletePhoto(index: number) {
    this.$onecentral.confirmPrompt().then(() => {
      // Update photos array
      // const index = this._photos.findIndex(x => x._uuid == photo._uuid);
      this._photos.splice(index, 1);

      // Update Vuex
      this.setMaintenancejobDeep({ path: this.path, data: this._photos })
        .then(() => this.$toasted.success("Photo removed"))
        .catch((err: any) => this.$onecentral.displayError(err));
    });
  }
}
